import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { FORM_GROUP_STATUS } from "../../model/forms/contract/form-group-contracts";
import {
  BrandingTypeEnum,
  FormTemplateStatus,
} from "../../model/forms/contract/form-template-contract";
import { FormTemplate } from "../../model/forms/form-template.model";
import { HasId } from "../../model/generics";
import { IQueryFilter, QueryResult } from "../../model/query.filter.class";
import {
  FORM_TEMPLATE_STATUS,
  TFormTemplateCounts,
} from "../../services/forms/contract/form-template.contract";
import { createUrl, getPublicRoutesHeaders, queryToParams } from "../api.util";
import { GenericApi, HasIdOrExternalValueOrExternalIdOrServiceName } from "../generic.api";

// Convenience Type
type T = FormTemplate;

export interface FormTemplateListItem {
  id: number;
  code: string;
  name: string;
  uuid?: string;
  formTemplateName: string;
  type: string;
  version: string;
  status: FormTemplateStatus;
  lastModified: string;
  count: number;
}

// change FormTemplateFilter  type
export interface FormTemplateFilter {
  status?: FormTemplateStatus;
  type?: "CFET" | "ISQ" | "SA" | "PAIS";
  formTemplateName?: string;
}

export interface FormTemplateOutput {
  isCFET: number;
  isSA: number;
  isISQ: number;
  id: number;
  uuid: string;
  name: string;
  description?: string | null;
  internalName: string;
  minResponsesForAbrv: number;
  minResponsesForBenchmarks: number;
  minResponsesForFull: number;
  showBenchmarkTotal: boolean;
  useIcons: boolean;
  minimumBenchmarkSampleSize: number;
  minQuestionResponsesForBenchmark: number;
  brandingType: BrandingTypeEnum;
  isAvailableOffline: number;
  timerInMinutes?: number | null;
  alertBeforeEndTimerInMinutes?: number | null;
  offlineThankyouMessage?: string | null;
  onlineThankyouMessage?: string | null;
  image: string;
  isDraft: number;
  isArchived: number;
  hasBeenPreviewed: boolean;
  formTemplateCategory?: FormTemplateCategory;
  formTemplateSections: FormTemplateSection[];
}

export interface QuestionnaireOverviewUsage {
  id: number;
  name: string;
  status: keyof typeof FORM_GROUP_STATUS;
}

export interface QuestionnaireOverview {
  id: number;
  uuid: string;
  name: string;
  status: keyof typeof FORM_TEMPLATE_STATUS;
  createdAt: string;
  createdBy: string | null;
  lastModifiedAt: string;
  lastModifiedBy: string | null;
  publishedAt: string | null;
  publishedBy: string | null;
  sourceQuestionnaireId: number | null;
  usage: QuestionnaireOverviewUsage[];
  auditObjectTypeId: number;
  auditObjectId: number;
}

export interface FormTemplateSection {
  id: number;
  name: string;
  order: number;
  formTemplateSectionType: {
    id: number;
    name: string;
  };
  formTemplateSectionVariants: FormTemplateSectionVariant[];
  formTemplateQuestions: FormTemplateQuestion[];
}

export interface FormTemplateQuestion {
  id: number;
  order: number;
  questionVariant: {
    id: number;
    text: string;
  };
}

export interface FormTemplateSectionVariant {
  id: number;
  text: string;
}

export interface FormTemplateCategory {
  id: number;
  name: string;
  description?: string | null;
}

export interface ICreateUpdateFormTemplateInput {
  id?: number;
  name: string;
  description?: string | null;
  internalName: string;
  minResponsesForAbrv: number;
  minResponsesForBenchmarks: number;
  minResponsesForFull: number;
  showBenchmarkTotal: boolean;
  useIcons: boolean;
  minimumBenchmarkSampleSize: number;
  minQuestionResponsesForBenchmark: number;
  brandingType: BrandingTypeEnum;
  isAvailableOffline: number;
  timerInMinutes?: number | null;
  alertBeforeEndTimerInMinutes?: number | null;
  offlineThankyouMessage?: string | null;
  onlineThankyouMessage?: string | null;
  image: string;
  formTemplateCategoryId: number;
  formTemplateCategoryName: string;
  formTemplateCategoryDescription?: string | null;
  isDraft: number;
  sections: IFormTemplateSectionInput[];
  isCFET: number;
  isSA: number;
  isISQ: number;
  hasBeenPreviewed: boolean;
}

export interface IFormTemplateSectionInput {
  id?: number;
  formTemplateSectionTypeId: number;
  order: number;

  value_method1?: string | null;
  value_method2?: string | null;

  variantDeliveryMethodId: number;
  variantLanguageId: number;

  questions: IFormTemplateQuestionInput[];
}

export interface IFormTemplateQuestionInput {
  id?: number;
  order: number;

  viewMeta?: any | null;
  required: number;

  qVariantId?: number;
  qRangeVariantGroupId?: number;
  qEnumValueVariantGroupId?: number;
}

export type FormTemplateValidationMode = "online" | "offline" | "both";

export const FORM_TEMPLATE_VALIDATION_ISSUE_CODE = {
  NO_QUESTION_SECTIONS: "NO_QUESTION_SECTIONS",
  EMPTY_QUESTION_SECTION: "EMPTY_QUESTION_SECTION",
  INVALID_QUESTION_VARIANT: "INVALID_QUESTION_VARIANT",
  MISSING_QUESTION_VARIANT: "MISSING_QUESTION_VARIANT",
  MISSING_RANGE_VARIANT_GROUP: "MISSING_RANGE_VARIANT_GROUP",
  MISSING_RANGE_WEIGHTING_GROUP: "MISSING_RANGE_WEIGHTING_GROUP",
  INCOMPLETE_RANGE_VARIANT_GROUP: "INCOMPLETE_RANGE_VARIANT_GROUP",
  MISSING_ENUM_VALUE_VARIANT_GROUP: "MISSING_ENUM_VALUE_VARIANT_GROUP",
  INCOMPLETE_ENUM_VALUE_VARIANT_GROUP: "INCOMPLETE_ENUM_VALUE_VARIANT_GROUP",
} as const;

export type FormTemplateValidationIssueCode =
  (typeof FORM_TEMPLATE_VALIDATION_ISSUE_CODE)[keyof typeof FORM_TEMPLATE_VALIDATION_ISSUE_CODE];

export interface FormTemplateValidationIssue {
  code: FormTemplateValidationIssueCode;
  mode: FormTemplateValidationMode;
  message: string;
  sectionId?: number;
  sectionOrder?: number;
  sectionName?: string;
  formTemplateQuestionId?: number;
  questionId?: number;
  questionOrder?: number;
  questionText?: string;
  qVariantId?: number;
  qVariantGroupId?: number;
  qRangeVariantGroupId?: number;
  qEnumValueVariantGroupId?: number;
}

export interface FormTemplateValidationModeResult {
  isValid: boolean;
  skipped: boolean;
  issueCount: number;
}

export interface FormTemplateValidationOutput {
  formTemplateId: number;
  formTemplateUUID: string;
  isValid: boolean;
  modes: {
    online: FormTemplateValidationModeResult;
    offline: FormTemplateValidationModeResult;
  };
  issues: FormTemplateValidationIssue[];
}

@Injectable({
  providedIn: "root",
})
export class FormTemplateApi extends GenericApi<T> {
  public path = "form-template";

  constructor(public httpClient: HttpClient) {
    super(httpClient);
  }

  /**
   * @description Get the formTemplate information by making a GET request with the formTemplateUUID.
   * @param {string} uuid - The formTemplateUUID to fetch
   * @returns {Promise<FormTemplate>} - A promise that resolves to the response object.
   * @readonly
   * @example
   * ```
   * formTemplateApi.getByUUID(<uuid>).subscribe({
   *   next: response => console.log(response), // The formTemplate response object
   *   error: error => console.error(error) // Handle error
   * });
   * ```
   */
  readonly getByUUID = (uuid: string): Observable<T & HasId<T>> => {
    return this.httpClient.get<T & HasId<T>>(createUrl(this.path, uuid), {
      headers: getPublicRoutesHeaders(),
    });
  };

  list = (
    query: IQueryFilter
  ): Observable<QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>> => {
    return this.httpClient.get<
      QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>
    >(createUrl(this.path), {
      params: queryToParams(query),
    });
  };

  readonly getForDropDown = (): Observable<
    QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>
  > => {
    return this.httpClient.get<
      QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>
    >(createUrl(this.path, "get-for-dropdown"), {});
  };

  readonly getAll = (): Observable<
    QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>
  > => {
    return this.httpClient.get<
      QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>
    >(createUrl(this.path, "get-all"), {});
  };

  readonly getFormTemplateVersionsById = (id: number): Observable<T & HasId<T>[]> => {
    return this.httpClient.get<T & HasId<T>[]>(createUrl(this.path, id, "versions"));
  };

  readonly doesFormTemplateHaveQuestionGroups = (uuid: string): Observable<Boolean> => {
    return this.httpClient.get<Boolean>(
      createUrl(this.path, "form-template-has-question-groups", uuid)
    );
  };

  copyFormTemplate(payload: {
    formTemplateUUID: string;
    formTemplateName: string;
    copyAllQuestions?: boolean;
    copyLayoutAndBrandingSettings?: boolean;
  }): Observable<{ formTemplateUUID: string }> {
    return this.httpClient.post<{ formTemplateUUID: string }>(
      createUrl(this.path, "copy"),
      payload
    );
  }

  createOrUpdateFormTemplate(
    input: ICreateUpdateFormTemplateInput
  ): Observable<{ id: number; uuid: string }> {
    return this.httpClient.post<{ id: number; uuid: string }>(createUrl(this.path), input);
  }

  getFormTemplateByUuid(uuid: string): Observable<FormTemplateOutput> {
    return this.httpClient.get<FormTemplateOutput>(
      createUrl(this.path, "get-form-template-for-edit", uuid)
    );
  }

  validateFormTemplate(
    uuid: string,
    includeOffline = false
  ): Observable<FormTemplateValidationOutput> {
    return this.httpClient.get<FormTemplateValidationOutput>(
      createUrl(this.path, "validate", uuid),
      {}
    );
  }

  getIntroImageSignedUrl(id: number): Observable<string | null> {
    return this.httpClient.get<string | null>(
      createUrl(this.path, "get-introimage-signed-url", id)
    );
  }

  getCounts(): Observable<TFormTemplateCounts> {
    return this.httpClient.get<TFormTemplateCounts>(createUrl(this.path, "counts"));
  }

  getQuestionnaireOverview(uuid: string): Observable<QuestionnaireOverview> {
    return this.httpClient.get<QuestionnaireOverview>(createUrl(this.path, "overview", uuid));
  }

  createPreview(
    formTemplateUUID: string,
    previewOffline: boolean,
    companyExternalId?: string
  ): Observable<string> {
    return this.httpClient.post<string>(createUrl(this.path, "preview"), {
      formTemplateUUID,
      previewOffline,
      companyExternalId,
    });
  }

  changeDraftStatus(uuid: string, isDraft: boolean): Observable<{ success: boolean }> {
    return this.httpClient.post<{ success: boolean }>(createUrl(this.path, "change-draft-status"), {
      formTemplateUUID: uuid,
      isDraft,
    });
  }

  changeArchiveStatus(uuid: string): Observable<{ success: boolean }> {
    return this.httpClient.post<{ success: boolean }>(
      createUrl(this.path, "change-archive-status"),
      {
        formTemplateUUID: uuid,
      }
    );
  }
  copyAndLinkFormTemplate(uuid: string): Observable<{ newFormTemplateUUID: string }> {
    return this.httpClient.post<{ newFormTemplateUUID: string }>(
      createUrl(this.path, "copy-and-link-formTemplate"),
      {
        formTemplateUUID: uuid,
      }
    );
  }

  hasAnswersByUUID(uuid: string): Observable<{ hasAnswers: boolean }> {
    return this.httpClient.get<{ hasAnswers: boolean }>(createUrl(this.path, "has-answers", uuid));
  }
}
